import {
  ClassroomLessonOutline,
  FeedbackOutline,
  HandshakeOutline,
  IntegrationOutline,
} from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';

interface CustomerInsightsProps {
  customerFeedback: string;
  lessonsLearned: string;
  partnerships: string[];
  integrations: string[];
}

const CustomerInsights = ({
  customerFeedback,
  lessonsLearned,
  partnerships,
  integrations,
}: CustomerInsightsProps) => {
  const feedbackPoints = customerFeedback
    ? customerFeedback
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

  const lessonsPoints = lessonsLearned
    ? lessonsLearned
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

  return (
    <Stack className="w-full xs:min-w-full xs:p-12 xs:mt-12" direction="col" gapY={32}>
      <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
        {/* Customer Feedback Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <FeedbackOutline
              fill="transparent"
              fillColorToken="icon-active"
              height={24}
              width={24}
            />
            <Typography variant="heading-xs">Customer Feedback</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {feedbackPoints.length > 0 ? (
              feedbackPoints.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== feedbackPoints.length - 1 && (
                    <div className="border-b border-border" />
                  )}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Lessons Learned Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <ClassroomLessonOutline
              fill="transparent"
              fillColorToken="icon-active"
              height={24}
              width={24}
            />
            <Typography variant="heading-xs">Lessons Learned</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {lessonsPoints.length > 0 ? (
              lessonsPoints.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== lessonsPoints.length - 1 && <div className="border-b border-border" />}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Partnerships Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <HandshakeOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Partnerships</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {partnerships.length > 0 ? (
              partnerships.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== partnerships.length - 1 && <div className="border-b border-border" />}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Integrations Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <IntegrationOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Integrations</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {integrations.length > 0 ? (
              integrations.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== integrations.length - 1 && <div className="border-b border-border" />}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CustomerInsights;
